/**
 * Cash vs Card Comparator capture form — /cash-vs-card, shown under the
 * comparison. Replaces the old inline LeadForm and the "email me the results"
 * pill.
 *
 * The comparator passes its inputs and result as `context` (see
 * CashVsCardComparator.jsx → buildLeadContext). Keys read here:
 *   category, cash_handling_cost_gbp_per_month,
 *   card_processing_cost_gbp_per_month, monthly_sales_gbp,
 *   average_transaction_value_gbp, card_pricing ("In person" | "Online"),
 *   left_at_default (labels of inputs the visitor never changed).
 * The comparator shows results straight away from pre-filled example figures,
 * so sales/ATV only pre-fill the CRM once the visitor has changed them.
 * Everything in context also lands in the Lead's Note.
 */
import { company, email, fullName, phone } from "./common";

const has = (v) => typeof v === "number" && Number.isFinite(v);
const PAYMENT_METHOD = { "In person": "Card machine", Online: "Online payments" };

/** Labels used in `left_at_default` for the inputs that feed CRM fields. */
export const CASH_VS_CARD_INPUT_LABELS = {
  monthlySales: "Monthly sales",
  averageTransactionValue: "Average transaction value",
};

const userEntered = (ctx, label) => !(ctx.left_at_default || []).includes(label);
const gbp = (n) => `£${n.toFixed(2)}`;

const toolCashVsCard = {
  id: "tool_cash_vs_card",
  label: "Cash vs Card Comparator",
  crm: { layout: "Standard", leadSource: "Website Calculator - Cash vs Card" },
  fields: [
    [fullName, company],
    [email, { ...phone, help: "We'll only call to discuss your results — never spam." }],
  ],
  submitLabel: "Get my free comparison",
  consent: { variant: "standard" },
  success: {
    title: "Thank you",
    body: "We've got your comparison and details. We'll be in touch shortly.",
  },
  contextToCrm: (ctx) => ({
    Monthly_Turnover: userEntered(ctx, CASH_VS_CARD_INPUT_LABELS.monthlySales) ? ctx.monthly_sales_gbp : undefined,
    ATV: userEntered(ctx, CASH_VS_CARD_INPUT_LABELS.averageTransactionValue)
      ? ctx.average_transaction_value_gbp
      : undefined,
    How_do_you_take_payments_now: PAYMENT_METHOD[ctx.card_pricing] ? [PAYMENT_METHOD[ctx.card_pricing]] : undefined,
    Needs: ["Card machine"],
  }),
  summary: (values, ctx) => {
    if (!has(ctx.cash_handling_cost_gbp_per_month) || !has(ctx.card_processing_cost_gbp_per_month)) {
      return "Cash vs Card Comparator";
    }
    const category = ctx.category ? ` (${ctx.category.toLowerCase()})` : "";
    return `Cash vs Card: cash ${gbp(ctx.cash_handling_cost_gbp_per_month)}/mo vs card ${gbp(ctx.card_processing_cost_gbp_per_month)}/mo${category}`;
  },
};

export default toolCashVsCard;
